import PocketBase, { type AuthRecord, ClientResponseError, type ListResult, type RecordSubscribeOptions, type RecordSubscription } from 'pocketbase';
import {
  computed,
  getCurrentInstance,
  type MaybeRef,
  onUnmounted,
  ReactiveEffect,
  type Reactive,
  reactive,
  ref,
  toRef,
  type UnwrapRef,
  watch,
  watchEffect,
} from 'vue';
import { useLogger } from '@/composables/use-logger.ts';
import { type CollectionRecords, Collections, type TypedPocketBase } from '@/types/pocketbase-types.ts';
import localforage from 'localforage';
import { jsonClone } from '@/utils/json-clone.ts';
import { generateUuid } from '@/utils/generate-uuid.ts';
import { createCachedResponse, isCachedResponse, shouldRetainCachedResponse, type CachedResponse } from '@/utils/offline-cache.ts';
import { readStoredString, removeStoredValue, writeStoredNumber, writeStoredString } from '@/utils/browser-storage.ts';

/**
 * A list of functions that can be used to refresh data for a given collection.
 * The key is the collection name, and the value is a { id: func } of functions that can be run to refresh data for that collection.
 */
const refreshFunctions: Record<string, Record<string, () => Promise<void>>> = {};

type ApiResponse<T> = Reactive<{
  /**
   * The data returned from the request.
   */
  data: T | undefined;
  /**
   * The current status of the request.
   *
   * - `idle`: The request has not been started yet.
   * - `loading`: The request is currently loading data.
   * - `success`: The request completed successfully.
   * - `error`: The request failed.
   */
  status: 'idle' | 'loading' | 'success' | 'error';
  /**
   * The current operation being performed.
   *
   * - `none`: No operation is being performed.
   * - `load-cache`: The request is loading data from the cache.
   * - `load-http`: The request is loading data from the server.
   * - `refresh`: The request is refreshing data from the server.
   */
  operation: 'none' | 'load-cache' | 'load-http' | 'refresh';
  /**
   * The error message, if any.
   */
  error: string;
  /**
   * Refresh the data by re-running the request.
   */
  refresh: () => Promise<void>;
  /**
   * Reset the response to its initial state.
   */
  reset: () => void;
}>;

type UsePocketBaseOptions<T> = {
  /**
   * Enable debug logging for this query.
   */
  debug?: boolean;
  /**
   * The cache key to use for this query.
   * If provided, the response will be cached locally and refreshed automatically when the cache expires.
   */
  cacheKey?: MaybeRef<string>;
  /**
   * Collection refresh key for a custom endpoint. Collection requests infer
   * this from their URL; custom endpoints are not registered unless this is
   * supplied explicitly.
   */
  refreshKey?: keyof CollectionRecords;
  /**
   * The maximum age of the cached response, in milliseconds.
   *
   * Defaults to 30 days.
   */
  cacheMaxAge?: number;
  /**
   * Whether the query should be run.
   *
   * Defaults to `true`
   */
  readyToRun?: MaybeRef<boolean>;
  /**
   * Whether to suppress error logging to avoid confusing developers for queries where we expect errors.
   *
   * Defaults to `false`
   */
  suppressError?: boolean;
  /**
   * If the query already has a result, and it is loading data from the cache or http, whether to keep the existing data when the new data is loaded.
   *
   * Defaults to `false`
   */
  keepDataWhileLoading?: boolean;
  /**
   * Debounce changes to the query.
   *
   * Will wait X ms before running the query again, to avoid triggering multiple requests when the user is typing.
   *
   * Cannot be combined with throttle.
   */
  debounce?: number;
  /**
   * Throttle changes to the query.
   *
   * Will only run the query once every X ms, regardless of how many times the user changes the query.
   *
   * Cannot be combined with debouncing.
   */
  throttle?: number;
  /**
   * Whether to run the query immediately after the component is mounted.
   *
   * Defaults to `true`.
   */
  runImmediately?: boolean;
  /**
   * The default value to use while loading data from the cache or http.
   *
   * Defaults to `undefined`.
   */
  defaultValue?: T;
  /**
   * Callback to run when the request starts.
   * @param data
   */
  onRequestStart?: () => void;
  /**
   * Callback to run when the request finishes regardless of success or failure.
   */
  onRequestFinish?: () => void;
  /**
   * Callback to run when the request succeeds.
   * @param data
   */
  onRequestSuccess?: (data: Readonly<T>, isRefresh: boolean) => void;
  /**
   * Callback to run when the request fails.
   * @param error
   */
  onRequestError?: (error: string) => void;
};

export type DbInsert<T> = Omit<T, 'id' | 'created' | 'updated'>;

const DEFAULT_CACHE_MAX_AGE = 1000 * 60 * 60 * 24 * 30; // 30 days
const IMPERSONATION_STORAGE_KEY = 'vennevent:admin-impersonation';
const ACCOUNT_PREVIEW_STORAGE_KEY = 'vennevent:admin-account-view-mode';

export type ImpersonationSession = {
  version: 1;
  status: 'starting' | 'active' | 'ending';
  sessionId: string;
  expiresAt: string;
  admin: {
    token: string;
    record: NonNullable<AuthRecord>;
  };
  target: {
    id: string;
    name: string;
    email: string;
  };
};

type ImpersonationResponse = {
  token: string;
  record: NonNullable<AuthRecord>;
  sessionId: string;
  expiresAt: string;
};

const pb = new PocketBase(window.location.origin) as TypedPocketBase;
const isLoggedIn = ref(false);
const currentUser = ref<AuthRecord | undefined>();
const impersonationSession = ref<ImpersonationSession>();
let impersonationExpiryTimer: ReturnType<typeof setTimeout> | undefined;

const impersonationStorageOptions = () => ({ key: IMPERSONATION_STORAGE_KEY, storage: () => window.localStorage, maxBytes: 65_536 });

function readImpersonationSession(value?: string | null): ImpersonationSession | undefined {
  const stored = value === undefined ? readStoredString(impersonationStorageOptions()) : value && value.length <= 65_536 ? value : undefined;
  if (!stored) return;
  try {
    const session = JSON.parse(stored) as Partial<ImpersonationSession>;
    if (
      session.version === 1 &&
      (session.status === 'starting' || session.status === 'active' || session.status === 'ending') &&
      typeof session.sessionId === 'string' &&
      session.sessionId.length > 0 &&
      session.sessionId.length <= 128 &&
      typeof session.expiresAt === 'string' &&
      session.expiresAt.length <= 64 &&
      Number.isFinite(new Date(session.expiresAt).getTime()) &&
      typeof session.admin?.token === 'string' &&
      session.admin.token.length > 0 &&
      session.admin.token.length <= 16_384 &&
      typeof session.admin.record?.id === 'string' &&
      session.admin.record.id.length > 0 &&
      session.admin.record.id.length <= 64 &&
      typeof session.target?.id === 'string' &&
      session.target.id.length > 0 &&
      session.target.id.length <= 64 &&
      typeof session.target.name === 'string' &&
      session.target.name.length <= 500 &&
      typeof session.target.email === 'string' &&
      session.target.email.length <= 500
    )
      return session as ImpersonationSession;
  } catch {
    // Invalid stored state is cleared during bootstrap.
  }
}

function persistImpersonationSession(session?: ImpersonationSession) {
  impersonationSession.value = session;
  if (session) return writeStoredString(impersonationStorageOptions(), JSON.stringify(session));
  return removeStoredValue(() => window.localStorage, IMPERSONATION_STORAGE_KEY);
}

function restoreStoredAdmin(session: ImpersonationSession) {
  pb.authStore.save(session.admin.token, session.admin.record);
  persistImpersonationSession();
}

function scheduleImpersonationExpiry() {
  if (impersonationExpiryTimer) clearTimeout(impersonationExpiryTimer);
  const session = impersonationSession.value;
  if (!session || session.status !== 'active') return;
  const delay = Math.max(0, new Date(session.expiresAt).getTime() - Date.now());
  impersonationExpiryTimer = setTimeout(() => void endImpersonation('/settings/admin'), delay);
}

async function startImpersonation(userId: string, reason: string) {
  if (impersonationSession.value) throw new Error('An impersonation session is already active.');
  const adminRecord = pb.authStore.record;
  if (!pb.authStore.token || !adminRecord || String(adminRecord.moderation_role) !== 'admin') throw new Error('Platform admin access is required.');
  const response = await pb.send<ImpersonationResponse>(`/api/custom/admin/users/${encodeURIComponent(userId)}/impersonate`, {
    method: 'POST',
    body: { reason },
    requestKey: null,
  });
  await clearOfflineData();
  removeStoredValue(() => window.sessionStorage, ACCOUNT_PREVIEW_STORAGE_KEY);
  const session: ImpersonationSession = {
    version: 1,
    status: 'starting',
    sessionId: response.sessionId,
    expiresAt: response.expiresAt,
    admin: { token: pb.authStore.token, record: adminRecord },
    target: {
      id: response.record.id,
      name: String(response.record.name || response.record.username || response.record.email || response.record.id),
      email: String(response.record.email || ''),
    },
  };
  if (!persistImpersonationSession(session)) {
    impersonationSession.value = undefined;
    throw new Error('Impersonation could not start because secure browser storage is unavailable.');
  }
  pb.authStore.save(response.token, response.record);
  persistImpersonationSession({ ...session, status: 'active' });
  window.location.assign('/events');
}

async function endImpersonation(returnUrl = '/settings/admin') {
  const session = impersonationSession.value ?? readImpersonationSession();
  if (!session) return;
  persistImpersonationSession({ ...session, status: 'ending' });
  await clearOfflineData();
  pb.authStore.save(session.admin.token, session.admin.record);
  try {
    await pb.send(`/api/custom/admin/impersonation/${encodeURIComponent(session.sessionId)}/end`, {
      method: 'POST',
      requestKey: null,
    });
    await pb.collection('users').authRefresh({ requestKey: null });
  } catch {
    pb.authStore.clear();
  } finally {
    persistImpersonationSession();
    window.location.assign(returnUrl);
  }
}

function logout() {
  const session = impersonationSession.value ?? readImpersonationSession();
  if (!session) {
    void clearOfflineData();
    pb.authStore.clear();
    return;
  }
  void (async () => {
    pb.authStore.save(session.admin.token, session.admin.record);
    try {
      await pb.send(`/api/custom/admin/impersonation/${encodeURIComponent(session.sessionId)}/end`, {
        method: 'POST',
        requestKey: null,
      });
    } catch {
      // The expiry job will close a session that cannot be ended during logout.
    }
    persistImpersonationSession();
    await clearOfflineData();
    pb.authStore.clear();
  })();
}

localforage.config({
  driver: localforage.INDEXEDDB,
  name: 'request-cache',
  version: 1,
  storeName: 'request_cache',
  description: 'Request cache for VennEvent',
});
void localforage.iterate((value: unknown, key) => {
  if (isCachedResponse(value)) {
    const age = Date.now() - value.$cachedAt;
    const maxAge = value.$cacheMaxAge ?? DEFAULT_CACHE_MAX_AGE;
    if (age > maxAge) {
      void localforage.removeItem(key);
      console.debug('Removed stale cache entry', key);
    }
  } else {
    void localforage.removeItem(key);
    console.debug('Removed incompatible cache entry', key);
  }
});

// Listen for changes to auth
pb.authStore.onChange(() => {
  isLoggedIn.value = pb.authStore.isValid;
  currentUser.value = pb.authStore.record;
}, true);

const storedImpersonation = readImpersonationSession();
if (storedImpersonation) {
  const isActiveTarget =
    storedImpersonation.status === 'active' &&
    new Date(storedImpersonation.expiresAt).getTime() > Date.now() &&
    pb.authStore.record?.id === storedImpersonation.target.id;
  if (isActiveTarget) {
    impersonationSession.value = storedImpersonation;
    scheduleImpersonationExpiry();
  } else {
    restoreStoredAdmin(storedImpersonation);
  }
} else {
  removeStoredValue(() => window.localStorage, IMPERSONATION_STORAGE_KEY);
}

window.addEventListener('storage', (event) => {
  if (event.key !== IMPERSONATION_STORAGE_KEY) return;
  const session = readImpersonationSession(event.newValue);
  if (session?.status === 'active' || session?.status === 'ending' || (!session && impersonationSession.value)) window.location.reload();
});

// Refresh only when a token is present. Calling auth-refresh for anonymous
// sessions creates a noisy 401 on every page load.
if (pb.authStore.token && !impersonationSession.value) {
  void pb
    .collection('users')
    .authRefresh()
    .catch(() => {
      pb.authStore.clear();
      console.debug('Refresh of auth token failed - cleared auth store.');
    });
}

export function usePB() {
  return {
    /**
     * The PocketBase instance.
     */
    db: pb,
    /**
     * Whether the user is currently logged in.
     */
    isLoggedIn,
    /**
     * The current authenticated user, if any.
     */
    currentUser,
    /**
     * Run a PocketBase query with reactive state management.
     *
     * @param T The type of the data returned from the query.
     * @param cb The callback that performs the PocketBase query.
     * @param options Optional query options.
     */
    query: usePocketBase,
    /**
     * Subscribe to changes in a PocketBase collection.
     *
     * @param collectionName The name of the collection to subscribe to.
     * @param cb The callback to call when a change occurs.
     * @param options Optional subscription options.
     */
    subscribe: usePocketBaseSubscription,
    /**
     * Cache management functions.
     */
    cache: {
      /**
       * Tag all cache entries for a collection as stale.
       * This means that the cache will be used, but a background refresh will be triggered.
       *
       * @param collectionName The name of the collection.
       */
      tagCollectionAsStale,
      /**
       * Tag a specific cache key as stale.
       * This means that the cache will be used, but a background refresh will be triggered.
       *
       * @param cacheKey The cache key to tag as stale.
       */
      tagCacheKeyAsStale,
      /**
       * Delete all cache entries for a collection.
       *
       * @param collectionName The name of the collection.
       */
      deleteCollection: deleteCollectionFromCache,
      /**
       * Delete a specific cache entry by its key.
       *
       * @param cacheKey The cache key to delete.
       */
      deleteKey: deleteKeyFromCache,
      /**
       * Clear the entire cache.
       */
      clearAll: clearCache,
    },
    /**
     * Log the user out.
     */
    logout,
    impersonation: {
      session: impersonationSession,
      isActive: computed(() => impersonationSession.value?.status === 'active'),
      start: startImpersonation,
      end: endImpersonation,
    },
  };
}

export async function refreshCollectionsThatStartsWith(...collectionNames: string[]) {
  const collectionsToRefresh = new Set<keyof CollectionRecords>();
  const availableCollectionNames = Object.values(Collections);
  collectionNames.forEach((name) => {
    availableCollectionNames.filter((collectionName) => collectionName.startsWith(name)).forEach((collectionName) => collectionsToRefresh.add(collectionName));
  });
  return refreshCollections(...Array.from(collectionsToRefresh));
}

/**
 * Tag all cache entries for a collection as stale and trigger a background refresh.
 * @param collectionNames The names of the collections to refresh.
 */
export async function refreshCollections(...collectionNames: (keyof CollectionRecords)[]) {
  return Promise.all(collectionNames.map((collectionName) => refreshCollection(collectionName)));
}

/**
 * Tag a specific collection as stale and trigger a background refresh.
 * @param collectionName
 */
function refreshCollection(collectionName: keyof CollectionRecords) {
  console.debug(`Refreshing collection "${collectionName}"...`);
  const stalePromise = tagCollectionAsStale(collectionName);
  const collectionFuncs = refreshFunctions[collectionName] || {};
  const refreshFuncs = Object.values(collectionFuncs || {});
  return Promise.all([stalePromise, ...refreshFuncs.map((refreshFunc) => refreshFunc())]);
}

export async function tagCollectionAsStale(collectionName: keyof CollectionRecords) {
  const staleItems: (CachedResponse<unknown> & { $localforageId: string })[] = [];
  await localforage.iterate((value: unknown, key: string) => {
    if (isCachedResponse(value) && value.$collection === collectionName) {
      staleItems.push({ ...value, $isStale: true, $localforageId: key });
    }
  });
  await Promise.all(
    staleItems.map((item) => {
      const key = item.$localforageId;
      // @ts-expect-error Remove localforageId before saving
      delete item.$localforageId;
      return localforage.setItem(key, item);
    }),
  );
}

export async function tagCacheKeyAsStale(cacheKey: string) {
  const cachedItem = await localforage.getItem<unknown>(cacheKey);
  if (isCachedResponse(cachedItem)) {
    cachedItem.$isStale = true;
    await localforage.setItem(cacheKey, cachedItem);
  }
}

export async function deleteCollectionFromCache(collectionName: keyof CollectionRecords) {
  const keysToDelete: string[] = [];
  await localforage.iterate((value: unknown, key: string) => {
    if (isCachedResponse(value) && value.$collection === collectionName) {
      keysToDelete.push(key);
    }
  });
  await Promise.all(keysToDelete.map((key) => localforage.removeItem(key)));
}

export async function deleteKeyFromCache(cacheKey: string) {
  await localforage.removeItem(cacheKey);
}

export async function clearCache() {
  await localforage.clear();
}

function markContentSync() {
  writeStoredNumber({ key: 'vennevent:last-content-sync', storage: () => window.localStorage, maxBytes: 32 }, Date.now());
  window.dispatchEvent(new Event('vennevent-content-sync'));
}

export async function clearOfflineData() {
  await clearCache();
  removeStoredValue(() => window.localStorage, 'vennevent:last-content-sync');
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration('/');
    registration?.active?.postMessage({ type: 'CLEAR_PRIVATE_CACHES' });
  }
}

/**
 * Run a PocketBase query with reactive state management.
 *
 * @param cb The callback that performs the PocketBase query. Will be called with the PocketBase instance, the current data, and a flag indicating whether the query is being refreshed.
 * @param options Optional query options.
 */
function usePocketBase<T>(cb: (db: PocketBase, data?: T, isRefreshing?: boolean) => Promise<T>, options: UsePocketBaseOptions<T> = {}): ApiResponse<T> {
  const logger = useLogger({
    prefix: 'usePocketBase',
    debug: options.debug,
  });
  const requestId = generateUuid();
  const readyToRun = toRef(options.readyToRun ?? true);
  const cacheKey = toRef(options.cacheKey ?? '');
  const cacheMaxAge = options.cacheMaxAge ?? DEFAULT_CACHE_MAX_AGE;
  const collectionName = ref('');
  const query = ref<{ [p: string]: string }>({});
  let nextExecutionId = 0;
  let activeExecution: { id: number; controller: AbortController } | undefined;
  let creatingExecution: { id: number; controller: AbortController } | undefined;
  let scheduleTimer: ReturnType<typeof setTimeout> | undefined;
  let lastThrottleRun: number | undefined;
  const ownedRefreshKeys = new Set<string>();

  function removeRefreshRegistration(key = collectionName.value) {
    if (!key || !refreshFunctions[key]?.[requestId]) return;
    logger.debug(`Removing refresh function for collection ${key}`);
    delete refreshFunctions[key]?.[requestId];
    if (Object.keys(refreshFunctions[key] || {}).length === 0) delete refreshFunctions[key];
  }

  function cancelScheduledRun() {
    if (scheduleTimer) clearTimeout(scheduleTimer);
    scheduleTimer = undefined;
  }

  function supersedeActiveExecution(reason: string) {
    nextExecutionId += 1;
    activeExecution?.controller.abort(reason);
    activeExecution = undefined;
  }

  // Clean up when the component is destroyed
  onUnmounted(() => {
    logger.debug('Component unmounted');
    cancelScheduledRun();
    queryEffect.stop();
    supersedeActiveExecution('Component unmounted');
    ownedRefreshKeys.forEach((key) => removeRefreshRegistration(key));
    ownedRefreshKeys.clear();
  });

  const pb = new PocketBase(window.location.origin);

  pb.beforeSend = function (url, sendOptions) {
    if (options.debug) {
      logger.debug('beforeSend', url, sendOptions);
    }

    const urlFragments = url.split('/api/collections/')[1];
    if (urlFragments) {
      // The first fragment is the collection name
      collectionName.value = urlFragments.split('/')[0]!;
      // Set the query filter for the collection
      query.value = sendOptions.query || {};
    }

    const refreshKey = options.refreshKey ?? collectionName.value;
    if (refreshKey) {
      refreshFunctions[refreshKey] ??= {};
      refreshFunctions[refreshKey]![requestId] = result.refresh;
      ownedRefreshKeys.add(refreshKey);
    }

    const cKey = cacheKey.value;
    if (cKey) {
      localforage
        .getItem<unknown>(cKey)
        .then((data) => {
          if (isCachedResponse<T>(data) && refreshKey) {
            data.$collection = refreshKey;
            void localforage.setItem(cKey, data);
            logger.debug('Updated cache metadata for key', cacheKey.value, {
              $collection: collectionName.value,
            });
          }
        })
        .catch(() => {
          logger.error('Skipped updating cache metadata for key', cacheKey.value);
        });
    }

    if (creatingExecution) sendOptions.signal = creatingExecution.controller.signal;
    return {
      url,
      sendOptions,
    };
  };

  const result: ApiResponse<T> = reactive({
    data: options.defaultValue as T,
    status: 'idle',
    operation: 'none',
    error: '',
    refresh: async () => {
      if (!readyToRun.value) {
        logger.debug('readyToRun is false, not running refresh');
        return;
      }
      logger.debug('refresh called');
      cancelScheduledRun();
      await startExecution('refresh', true);
    },
    reset: () => {
      logger.debug('reset called');
      cancelScheduledRun();
      supersedeActiveExecution('Reset called');
      if (isSearchResult(result.data)) {
        result.data.items = [];
        result.data.page = 1;
      } else {
        result.data = undefined;
      }
      result.status = 'idle';
      result.operation = 'none';
      result.error = '';
    },
  });

  watchEffect(
    () => {
      logger.debug('Status changed to', result.status);
    },
    { flush: 'sync' },
  );

  watch(
    () => [cacheKey.value, readyToRun.value] as [string, boolean],
    async ([cacheKey, readyToRun]) => {
      if (!cacheKey || !readyToRun) {
        return;
      }
      const cachedResponse = await localforage.getItem<unknown>(cacheKey);
      if (isCachedResponse<T>(cachedResponse)) {
        const isCacheExpired = Date.now() - cachedResponse.$cachedAt > cacheMaxAge;
        if (isCacheExpired) {
          await localforage.removeItem(cacheKey);
        }
      } else if (cachedResponse) {
        await localforage.removeItem(cacheKey);
      }
    },
  );

  let clonedData = jsonClone(result.data as T);
  watchEffect(
    () => {
      clonedData = jsonClone(result.data as T);
    },
    { flush: 'sync' },
  );

  const queryEffect = new ReactiveEffect(() => {
    if (!readyToRun.value) {
      logger.debug('readyToRun is false, not running query');
      cancelScheduledRun();
      supersedeActiveExecution('Query is not ready');
      result.operation = 'none';
      result.status = result.data === undefined ? 'idle' : 'success';
      return;
    }

    void startExecution('load-http', false);
  });

  queryEffect.scheduler = scheduleQueryRun;

  function scheduleQueryRun() {
    if (!readyToRun.value) {
      queryEffect.run();
      return;
    }
    if (options.debounce) {
      cancelScheduledRun();
      scheduleTimer = setTimeout(() => {
        scheduleTimer = undefined;
        queryEffect.run();
      }, options.debounce);
      return;
    }
    if (options.throttle) {
      const remaining = lastThrottleRun === undefined ? 0 : options.throttle - (Date.now() - lastThrottleRun);
      if (remaining > 0) {
        if (!scheduleTimer) {
          scheduleTimer = setTimeout(() => {
            scheduleTimer = undefined;
            lastThrottleRun = Date.now();
            queryEffect.run();
          }, remaining);
        }
        return;
      }
      lastThrottleRun = Date.now();
    }
    queryEffect.run();
  }

  async function startExecution(operation: ApiResponse<T>['operation'], isRefresh: boolean) {
    const execution = { id: ++nextExecutionId, controller: new AbortController() };
    activeExecution?.controller.abort('Query superseded');
    activeExecution = execution;

    options?.onRequestStart?.();
    result.status = 'loading';
    result.operation = operation;

    creatingExecution = execution;
    let responsePromise: Promise<T>;
    try {
      responsePromise = Promise.resolve(cb(pb, clonedData, isRefresh));
    } catch (error) {
      responsePromise = Promise.reject(error);
    } finally {
      creatingExecution = undefined;
    }
    // Attach both handlers before any cache work so a quickly rejected or
    // superseded request can never become an unhandled rejection.
    const settledResponse = responsePromise.then(
      (value) => ({ ok: true as const, value }),
      (error: unknown) => ({ ok: false as const, error }),
    );

    // Load from the cache if it exists
    const executionCacheKey = cacheKey.value;
    if (!isRefresh && executionCacheKey) {
      result.operation = 'load-cache';
      try {
        const cachedResponse = await localforage.getItem<unknown>(executionCacheKey);
        if (activeExecution?.id !== execution.id) return;
        if (isCachedResponse<T>(cachedResponse)) {
          const isCacheExpired = Date.now() - cachedResponse.$cachedAt > cacheMaxAge;
          if (isCacheExpired) {
            await localforage.removeItem(executionCacheKey);
            logger.debug('Removed stale cached response', executionCacheKey);
          } else {
            logger.debug('Cache hit for key', executionCacheKey, cachedResponse);
            result.data = cachedResponse.$data as UnwrapRef<T>;
            result.status = 'success';
            result.error = '';
          }
        } else if (cachedResponse) {
          await localforage.removeItem(executionCacheKey);
          logger.debug('Removed incompatible cached response', executionCacheKey);
        } else {
          logger.debug('No cached response found');
        }
      } catch {
        logger.debug('No cached response found');
      }
    }

    logger.debug('runRequest called');
    if (activeExecution?.id === execution.id) result.operation = operation;
    const response = await settledResponse;
    if (activeExecution?.id !== execution.id) return;

    if (response.ok) {
      const responseValue = response.value;
      logger.debug('runRequest success', response);
      result.status = 'success';
      result.operation = 'none';
      if (JSON.stringify(responseValue) === JSON.stringify(result.data)) {
        logger.debug('No change in response, not updating result.data');
      } else {
        result.data = responseValue as UnwrapRef<T>;
      }
      result.error = '';

      if (executionCacheKey) {
        try {
          const valueToCache = createCachedResponse(responseValue, cacheMaxAge);
          await localforage.setItem(executionCacheKey, valueToCache);
          if (activeExecution?.id !== execution.id) return;
          markContentSync();
          logger.debug('Response saved in cache for key', executionCacheKey);
        } catch (error) {
          logger.error('Failed to cache response for key', executionCacheKey, error);
        }
      }

      options?.onRequestSuccess?.(result.data as T, isRefresh);
      options?.onRequestFinish?.();
    } else {
      const errorResponse = response.error;
      if (shouldRetainCachedResponse(navigator.onLine, result.data)) {
        logger.debug('Offline request failed; retaining cached response', executionCacheKey);
        result.status = 'success';
        result.operation = 'none';
        result.error = '';
        options?.onRequestFinish?.();
        return;
      }
      if (execution.controller.signal.aborted || (errorResponse instanceof ClientResponseError && errorResponse.status === 0)) {
        logger.debug('runRequest aborted', errorResponse);
        result.status = 'idle';
        result.operation = 'none';
        return;
      }
      if (!options.suppressError) {
        logger.error('runRequest error for collection', `"${collectionName.value}"`, 'with query', query.value, errorResponse);
      }
      // If the request failed, clear the cache (if it exists) and set the result to an error state
      // (this will trigger a refresh to try again, which will also clear the cache if it exists and the request succeeds again)
      if (executionCacheKey) {
        await localforage.removeItem(executionCacheKey).catch((error) => logger.error('Failed to remove stale cache entry', executionCacheKey, error));
        if (activeExecution?.id !== execution.id) return;
      }

      result.data = undefined;
      result.operation = 'none';
      result.status = 'error';
      const errorMessage = errorResponse instanceof Error ? errorResponse.message : String(errorResponse);
      result.error = errorMessage;

      options?.onRequestError?.(errorMessage);
      options?.onRequestFinish?.();
    }
  }

  if (options.runImmediately !== false) scheduleQueryRun();

  return result;
}

function isSearchResult(response: unknown): response is ListResult<{ id: string }> {
  if (typeof response !== 'object' || response === null) return false;
  const r = response as Partial<ListResult<unknown>>;
  return Array.isArray(r.items) && typeof r.page === 'number' && typeof r.totalPages === 'number' && r.totalPages !== -1;
}

function usePocketBaseSubscription<CollectionType>(
  collectionName: keyof CollectionRecords,
  cb: (event: RecordSubscription<CollectionType>) => void,
  options?: RecordSubscribeOptions | ((db: TypedPocketBase, user: NonNullable<AuthRecord>) => RecordSubscribeOptions),
  ownership: 'component' | 'manual' = 'component',
): () => void {
  const logger = useLogger({ prefix: 'usePocketBaseSubscription' });
  const instance = getCurrentInstance();
  let stopped = false;
  let generation = 0;
  let activeUnsubscribe: (() => void) | undefined;

  function replaceSubscription(user: AuthRecord | null | undefined) {
    const ownedGeneration = ++generation;
    void activeUnsubscribe?.();
    activeUnsubscribe = undefined;
    if (!user || stopped) return;

    const subscriptionOptions = typeof options === 'function' ? options(pb, user) : options;
    void pb
      .collection(collectionName)
      .subscribe(
        '*',
        (event) => {
          if (!stopped && generation === ownedGeneration) cb(event as RecordSubscription<CollectionType>);
        },
        subscriptionOptions,
      )
      .then((unsubscribe) => {
        if (stopped || generation !== ownedGeneration) {
          void unsubscribe();
          return;
        }
        activeUnsubscribe = unsubscribe;
      })
      .catch((error) => {
        if (!stopped && generation === ownedGeneration) logger.error(`Failed to subscribe to collection "${collectionName}"`, error);
      });
  }

  const stopWatching = watch(
    () => (isLoggedIn.value ? currentUser.value?.id : undefined),
    (userId) => replaceSubscription(userId ? currentUser.value : undefined),
    { immediate: true, flush: 'sync' },
  );

  const stop = () => {
    if (stopped) return;
    stopped = true;
    generation += 1;
    stopWatching();
    void activeUnsubscribe?.();
    activeUnsubscribe = undefined;
  };

  if (instance && ownership === 'component') onUnmounted(stop);
  return stop;
}
